import { FormItem, Input, Label, Text, WrappingType } from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import {
  IssueType,
  CorporateContentQueryDetailsFragment,
  useUpdateCorporateContentQueryMutation
} from 'generated/graphql';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useUpdateCorporateContentQueryStore } from 'stores/UpdateCorporateContentQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { validateString } from 'utils/validator';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';

export interface DisplayQueryDetailsCorporateContentProps {
  queryId: string;
  queryDetails: CorporateContentQueryDetailsFragment;
}

export const DisplayQueryDetailsCorporateContent = forwardRef<
  SpecificQueryType,
  DisplayQueryDetailsCorporateContentProps
>((props: DisplayQueryDetailsCorporateContentProps, ref) => {
  const { queryDetails, queryId } = props;
  const { t } = useTranslation();
  const corporateContentQueryStore = useUpdateCorporateContentQueryStore();
  const baseQueryStore = useUpdateBaseQueryStore();
  const { editMode, setCancelEditing } = useEditQueryStore();
  const [, callUpdateQuery] = useUpdateCorporateContentQueryMutation();
  const classes = useStyles({ editMode });

  useImperativeHandle(ref, () => ({
    cancel: () => {
      corporateContentQueryStore.setInitialStateWithProps(queryDetails);
    },
    save: () => {
      return saveQuery();
    },
    isValid: () => {
      return validateAllFields();
    }
  }));

  useEffect(() => {
    corporateContentQueryStore.setInitialStateWithProps(queryDetails);
  }, []);

  const onChangeXtmProjectName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    corporateContentQueryStore.setXtmProjectName(value);
  };

  const onChangeFileName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    corporateContentQueryStore.setFileName(value);
    validateString(value, corporateContentQueryStore.setFileNameValueState);
  };

  const saveQuery = async () => {
    const result = await callUpdateQuery({
      input: {
        id: Number(queryId),
        sourceText: baseQueryStore.sourceText,
        targetText: baseQueryStore.targetText,
        deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
        targetLanguage: baseQueryStore.targetLanguage,
        issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
        details: baseQueryStore.details,
        ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
        distributions: baseQueryStore.distributions,
        resetedDistributions: baseQueryStore.resetedDistributions,
        fileName: corporateContentQueryStore.fileName,
        xtmProjectName: corporateContentQueryStore.xtmProjectName,
        qeReview: baseQueryStore.qeReview
      }
    });
    return { error: result.error };
  };

  const validateAllFields = () => {
    return validateFileName();
  };

  const validateFileName = () => {
    return validateString(
      corporateContentQueryStore.fileName,
      corporateContentQueryStore.setFileNameValueState
    );
  };

  const getDetailsData = (): FormDisplayItem[] => {
    return [
      {
        renderComponent: () => (
          <Input
            data-testid="edit-xtm-project-name"
            data-help-id="edit-xtm-project-name"
            aria-labelledby={t('display-query-details-corporate-content-xtm-project-name')}
            value={corporateContentQueryStore.xtmProjectName}
            className={classes.formLineInput}
            onChange={onChangeXtmProjectName}
          />
        ),
        dataTestId: 'xtm-project-name',
        data: corporateContentQueryStore.xtmProjectName,
        i18nTag: t('display-query-details-corporate-content-xtm-project-name')
      },
      {
        renderComponent: () => (
          <Input
            value={corporateContentQueryStore.fileName}
            aria-labelledby={t('display-query-details-corporate-content-file-name')}
            data-testid="edit-file-name"
            data-help-id="edit-file-name"
            className={classes.formLineInput}
            valueState={corporateContentQueryStore.fileNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-name')}
              </Label>
            }
            onChange={onChangeFileName}
          />
        ),
        dataTestId: 'file-name',
        data: corporateContentQueryStore.fileName,
        i18nTag: t('display-query-details-corporate-content-file-name'),
        required: true
      }
    ];
  };

  let queryDetailsData: FormDisplayItem[] = getDetailsData();

  useEffect(() => {
    queryDetailsData = getDetailsData();
  }, [queryDetails]);

  return (
    <>
      {queryDetailsData.map((entry) => (
        <FormItem
          key={entry.dataTestId}
          label={
            <Label
              id={entry.i18nTag}
              required={editMode && entry.required}
              data-help-id={entry.dataTestId}
              className={classes.formItemLabel}
            >
              {entry.i18nTag}
            </Label>
          }
        >
          {editMode && entry.editable !== false ? (
            entry.renderComponent()
          ) : (
            <Text
              data-testid={entry.dataTestId}
              wrapping
              aria-labelledby={entry.i18nTag}
              className={classes.formItemText}
            >
              {entry.data}
            </Text>
          )}
        </FormItem>
      ))}
    </>
  );
});
